import { useEffect, useRef, useState } from "react";
import { Plus, Receipt, Sparkle, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { formatINR } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STATUS_COLOR = { active: "#3A86FF", completed: "#2A9D8F", cancelled: "#0A0A0A", on_hold: "#F4A261" };

const empty = {
  po_number: "", title: "", type: "work_order", client_id: null,
  currency: "INR", value: 0, fx_rate: 1,
  start_date: "", end_date: "",
  status: "active", description: "",
};

export default function WorkOrders() {
  const { active, activeId } = useCompany();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [extracting, setExtracting] = useState(false);
  const extractRef = useRef(null);

  const load = async () => {
    if (!activeId) { setList([]); return; }
    const { data } = await api.get("/finance/work-orders", { params: { company_id: activeId } });
    setList(data);
    const { data: cs } = await api.get("/finance/contacts", { params: { company_id: activeId, type: "client" } });
    setClients(cs);
  };

  useEffect(() => { load(); }, [activeId]);

  const openNew = () => { setForm(empty); setOpen(true); };

  const save = async () => {
    if (!form.po_number || !form.title) { toast.error("PO/WO number and title required"); return; }
    const payload = {
      ...form, company_id: activeId,
      value: parseFloat(form.value || 0),
      fx_rate: parseFloat(form.fx_rate || 1),
      client_id: form.client_id || null,
    };
    const { data } = await api.post("/finance/work-orders", payload);
    toast.success("Work order created");
    setOpen(false);
    navigate(`/finance/work-orders/${data.id}`);
  };

  const handleExtract = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setExtracting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/finance/extract?kind=work_order", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((curr) => ({
        ...curr,
        po_number: data.po_number || curr.po_number,
        title: data.title || curr.title,
        value: data.value ?? curr.value,
        currency: (data.currency === "USD" ? "USD" : "INR"),
        start_date: data.start_date || curr.start_date,
        end_date: data.end_date || curr.end_date,
        description: data.description || curr.description,
      }));
      toast.success("AI extracted fields");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction failed");
    } finally {
      setExtracting(false); e.target.value = "";
    }
  };

  if (!activeId) {
    return (
      <Layout title="Work Orders">
        <div className="border border-black/10 p-16 text-center">
          <Buildings size={36} className="mx-auto text-[#D90429]" />
          <h3 className="font-display text-2xl font-bold tracking-tight mt-4">Pick a company first</h3>
          <button onClick={() => navigate("/finance/companies")} className="mt-5 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Go to Companies</button>
        </div>
      </Layout>
    );
  }

  const fmt = (n) => formatINR(n);

  return (
    <Layout
      title="Work Orders"
      subtitle={`${list.length} order${list.length === 1 ? "" : "s"} · ${active?.name || ""}`}
      actions={<button onClick={openNew} data-testid="add-wo-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] active:scale-95"><Plus size={16} weight="bold" /> New Work Order</button>}
    >
      {list.length === 0 ? (
        <div className="border border-black/10 p-16 text-center text-[#717680]"><Receipt size={36} className="mx-auto text-[#D90429]"/><p className="mt-4">No work orders yet. Create your first to start tracking payments & expenses.</p></div>
      ) : (
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="work-orders-table">
            <thead className="bg-[#F5F6F8] text-left"><tr>{["PO #","Title","Client","Type","Value","Received","Expenses","Net P/L","Status"].map(h=>(<th key={h} className="px-4 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">{h}</th>))}</tr></thead>
            <tbody>
              {list.map(wo=>{
                const s = wo.summary || {};
                const client = clients.find(c=>c.id===wo.client_id);
                return (
                  <tr key={wo.id} className="border-t border-black/5 hover:bg-[#FAFBFC] cursor-pointer" onClick={()=>navigate(`/finance/work-orders/${wo.id}`)} data-testid={`wo-row-${wo.id}`}>
                    <td className="px-4 py-3 font-mono-shelv text-xs">{wo.po_number}</td>
                    <td className="px-4 py-3 font-semibold">{wo.title}</td>
                    <td className="px-4 py-3 text-[#4A4D57]">{client?.name || "—"}</td>
                    <td className="px-4 py-3 text-xs font-mono-shelv uppercase">{wo.type.replace("_"," ")}</td>
                    <td className="px-4 py-3 font-mono-shelv">{fmt(s.value_inr)}</td>
                    <td className="px-4 py-3 font-mono-shelv text-[#2A9D8F]">{fmt(s.total_payments_inr)}</td>
                    <td className="px-4 py-3 font-mono-shelv text-[#D90429]">{fmt(s.total_expenses_inr)}</td>
                    <td className={`px-4 py-3 font-mono-shelv font-bold ${s.net_profit_inr >= 0 ? "text-[#2A9D8F]" : "text-[#D90429]"}`}>{fmt(s.net_profit_inr)}</td>
                    <td className="px-4 py-3"><span className="text-xs font-mono-shelv uppercase px-2 py-0.5" style={{background:(STATUS_COLOR[wo.status]||"#0A0A0A")+"15", color:STATUS_COLOR[wo.status]||"#0A0A0A"}}>{wo.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">New work order</DialogTitle></DialogHeader>

          <div className="flex items-center justify-between border-b border-black/5 pb-3 mb-1">
            <p className="text-xs text-[#717680]">Tip: upload the scanned PO/WO PDF — AI will auto-fill these fields.</p>
            <input ref={extractRef} type="file" accept="application/pdf,image/*" onChange={handleExtract} className="hidden" data-testid="extract-wo-input"/>
            <button onClick={()=>extractRef.current?.click()} disabled={extracting} data-testid="extract-wo-button" className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#D90429] disabled:opacity-50">
              <Sparkle size={14} weight="fill"/> {extracting ? "Reading PDF…" : "AI extract from PDF"}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><Label>PO / WO Number *</Label><Input data-testid="wo-po-number" value={form.po_number} onChange={(e)=>setForm({...form, po_number:e.target.value})} className="rounded-none"/></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v)=>setForm({...form, type:v})}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="purchase_order">Purchase Order</SelectItem>
                  <SelectItem value="work_order">Work Order</SelectItem>
                  <SelectItem value="tender">Tender</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Title *</Label><Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} className="rounded-none"/></div>
            <div>
              <Label>Client</Label>
              <Select value={form.client_id || "none"} onValueChange={(v)=>setForm({...form, client_id: v==="none"?null:v})}>
                <SelectTrigger className="rounded-none"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {clients.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v)=>setForm({...form, status:v})}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent>{Object.keys(STATUS_COLOR).map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v)=>setForm({...form, currency:v, fx_rate: v==="INR" ? 1 : form.fx_rate})}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="INR">INR ₹</SelectItem><SelectItem value="USD">USD $</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Value</Label><Input type="number" value={form.value} onChange={(e)=>setForm({...form, value:e.target.value})} className="rounded-none"/></div>
            {form.currency === "USD" && (
              <div className="col-span-2"><Label>USD → INR rate</Label><Input type="number" value={form.fx_rate} onChange={(e)=>setForm({...form, fx_rate:e.target.value})} className="rounded-none"/></div>
            )}
            <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e)=>setForm({...form, start_date:e.target.value})} className="rounded-none"/></div>
            <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e)=>setForm({...form, end_date:e.target.value})} className="rounded-none"/></div>
            <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} className="rounded-none"/></div>
          </div>
          <DialogFooter>
            <button onClick={()=>setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} data-testid="save-wo-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Create</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
